import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CalendarDays, UserRound } from 'lucide-react'
import { api } from '../../../lib/api'
import { Modal } from '../../ui/Modal'
import { eroare } from './comun'
import { FormularCerere } from './FormularCerere'

/**
 * „Cerere nouă” din ecranul „Cereri de marfă” (26.10.2026).
 *
 * Victor: oricine vrea marfă face cerere în CRM. Până acum o cerere se putea face doar din fila
 * Necesar a unei activități, iar vânzările nu văd lista de activități — Gabriela n-avea de unde
 * cere materiale pentru farmacii. Primul pas alege pentru cine e marfa, apoi se deschide același
 * formular ca pe pagina activității:
 *
 *  - „Pentru mine”: marfa pe care o duci tu în teren. Se trece pe activitatea automată
 *    „Necesar teren · Tn AAAA”, creată la prima cerere a trimestrului;
 *  - „Pentru o activitate”: un eveniment, o campanie, un barter — de acolo și cererile către un
 *    colaborator sau un partener, ca pe pagina activității.
 */

interface ActivitateScurta {
  id: number
  cod: string
  titlu: string
  generatAutomat?: boolean
}

type Ales = { campanieId: number; teren: boolean }

export function CerereNoua({ onClose, onGata }: { onClose: () => void; onGata: () => void }) {
  const [ales, setAles] = useState<Ales | null>(null)
  const [cuActivitate, setCuActivitate] = useState(false)
  const [cauta, setCauta] = useState('')

  const teren = useMutation({
    mutationFn: async () =>
      (await api.post<{ campanie: ActivitateScurta }>('/marketing/necesar/teren/container', {}))
        .data.campanie,
    onSuccess: (c) => setAles({ campanieId: c.id, teren: true }),
    onError: eroare,
  })

  const activitati = useQuery<{ campanii: ActivitateScurta[] }>({
    queryKey: ['campanii-deschise'],
    queryFn: async () => (await api.get('/campanii', { params: { deschise: 'true' } })).data,
    enabled: cuActivitate,
  })
  const termen = cauta.trim().toLowerCase()
  const lista = useMemo(
    () =>
      (activitati.data?.campanii ?? [])
        // Programele automate n-au ce căuta aici: „pentru mine” e drumul lor.
        .filter((a) => !a.generatAutomat)
        .filter((a) => !termen || `${a.cod} ${a.titlu}`.toLowerCase().includes(termen)),
    [activitati.data, termen],
  )

  if (ales)
    return (
      <FormularCerere
        campanieId={ales.campanieId}
        teren={ales.teren}
        cuTrimitere
        onClose={onClose}
        onGata={onGata}
      />
    )

  return (
    <Modal open onClose={onClose} size="lg" title="Cerere nouă de marfă">
      <div className="grid gap-4">
        <p className="text-sm text-b4l-text-muted">Pentru cine e marfa?</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => teren.mutate()}
            disabled={teren.isPending}
            className="grid min-h-[96px] gap-1 rounded-xl border border-b4l-border bg-b4l-surface p-4 text-left hover:border-b4l-blue hover:bg-b4l-blue-light/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-b4l-blue disabled:opacity-60"
          >
            <span className="flex items-center gap-2 font-semibold text-b4l-text">
              <UserRound size={18} aria-hidden="true" />
              Pentru mine
            </span>
            <span className="text-mic text-b4l-text-muted">
              Mostre și materiale pe care le duci tu în teren — farmacii, medici, vizite.
            </span>
          </button>
          <button
            type="button"
            onClick={() => setCuActivitate(true)}
            aria-expanded={cuActivitate}
            className={`grid min-h-[96px] gap-1 rounded-xl border p-4 text-left hover:border-b4l-blue hover:bg-b4l-blue-light/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-b4l-blue ${
              cuActivitate
                ? 'border-b4l-blue bg-b4l-blue-light/40'
                : 'border-b4l-border bg-b4l-surface'
            }`}
          >
            <span className="flex items-center gap-2 font-semibold text-b4l-text">
              <CalendarDays size={18} aria-hidden="true" />
              Pentru o activitate
            </span>
            <span className="text-mic text-b4l-text-muted">
              Eveniment, campanie, barter, sponsorizare — și marfa către un colaborator sau un
              partener.
            </span>
          </button>
        </div>

        {cuActivitate && (
          <div className="grid gap-2">
            <label className="grid gap-1 text-xs text-b4l-text-muted">
              Caută activitatea
              <input
                autoFocus
                value={cauta}
                onChange={(e) => setCauta(e.target.value)}
                placeholder="cod sau nume, ex. EVT-2026-031"
                className="rounded border border-b4l-border px-2 py-1.5 text-sm"
              />
            </label>
            <ul
              role="list"
              className="max-h-72 divide-y divide-b4l-border overflow-y-auto rounded border border-b4l-border"
            >
              {activitati.isLoading && (
                <li className="px-3 py-2 text-sm text-b4l-text-dim">Se încarcă…</li>
              )}
              {!activitati.isLoading && lista.length === 0 && (
                <li className="px-3 py-2 text-sm text-b4l-text-dim">
                  Nicio activitate deschisă cu numele ăsta.
                </li>
              )}
              {lista.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => setAles({ campanieId: a.id, teren: false })}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-b4l-bg-soft focus-visible:bg-b4l-bg-soft focus-visible:outline-none"
                  >
                    <span className="font-medium text-b4l-text">{a.titlu}</span>{' '}
                    <span className="text-mic text-b4l-text-dim">{a.cod}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  )
}
